import React, { useEffect, useState } from 'react';
import { Save, Upload, Image as ImageIcon } from 'lucide-react';
import api from '../utils/api';

interface CategoryCardItem {
  category: string;
  title: string;
  description: string;
  imageUrl: string;
  hasCustomImage: boolean;
  listingCount: number;
}

const isImageUrl = (url: string): boolean => url.startsWith('http') || url.startsWith('data:');

const getImageUrl = (imagePath: string): string => {
  if (!imagePath || imagePath === 'undefined' || imagePath === 'null') {
    return '';
  }

  if (isImageUrl(imagePath)) return imagePath;

  const normalizedPath = imagePath.replace(/\\/g, '/');
  const filename = normalizedPath.split('/').pop();
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  return `${apiUrl}/api/products/image/${filename}`;
};

const AdminCategoryCards: React.FC = () => {
  const [cards, setCards] = useState<CategoryCardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingCategory, setSavingCategory] = useState<string | null>(null);
  const [uploadingCategory, setUploadingCategory] = useState<string | null>(null);

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/category-cards/admin');
      setCards(response.data || []);
    } catch (error) {
      console.error('Error fetching category cards:', error);
      alert('فشل تحميل الأقسام.');
    } finally {
      setLoading(false);
    }
  };

  const updateLocalCard = (category: string, updates: Partial<CategoryCardItem>) => {
    setCards((prev) =>
      prev.map((card) => (card.category === category ? { ...card, ...updates } : card))
    );
  };

  const saveCard = async (card: CategoryCardItem) => {
    setSavingCategory(card.category);
    try {
      const response = await api.put('/api/category-cards/admin', {
        category: card.category,
        title: card.title,
        description: card.description,
        imageUrl: card.imageUrl
      });

      if (response.data) {
        updateLocalCard(card.category, response.data);
      }
    } catch (error) {
      console.error('Error saving category card:', error);
      alert('فشل حفظ القسم.');
    } finally {
      setSavingCategory(null);
    }
  };

  const uploadImage = async (category: string, file: File) => {
    setUploadingCategory(category);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await api.post('/api/products/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      updateLocalCard(category, { imageUrl: response.data.imageUrl, hasCustomImage: true });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('فشل رفع الصورة.');
    } finally {
      setUploadingCategory(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg min-h-screen transition-colors duration-300" dir="rtl">
        <div className="text-center text-gray-600 dark:text-dark-textSecondary">جاري تحميل الأقسام...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg min-h-screen transition-colors duration-300" dir="rtl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text">تعديل الاقسام</h1>
        <p className="text-gray-600 dark:text-dark-textSecondary mt-2">
          يمكنك تعديل عنوان ووصف وصورة كل قسم. إذا كانت الصورة فارغة سيتم استخدام صورة عشوائية من المنتجات.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {cards.map((card) => (
          <div
            key={card.category}
            className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border shadow-sm p-5 transition-colors duration-300"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text">{card.category}</h2>
              <span className="text-xs px-2 py-1 rounded-full bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300">
                {card.listingCount} منتج
              </span>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">العنوان</label>
              <input
                type="text"
                value={card.title}
                onChange={(e) => updateLocalCard(card.category, { title: e.target.value })}
                className="w-full p-3 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">الوصف</label>
              <textarea
                value={card.description}
                onChange={(e) => updateLocalCard(card.category, { description: e.target.value })}
                rows={3}
                className="w-full p-3 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">الصورة</label>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center px-3 py-2 rounded-lg bg-primary-600 text-white cursor-pointer hover:bg-primary-700 transition-colors">
                  <Upload className="h-4 w-4 ml-2" />
                  {uploadingCategory === card.category ? 'جاري الرفع...' : 'رفع صورة'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        uploadImage(card.category, file);
                      }
                    }}
                  />
                </label>

                <button
                  type="button"
                  onClick={() => updateLocalCard(card.category, { imageUrl: '', hasCustomImage: false })}
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-dark-border text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors"
                >
                  إزالة الصورة المخصصة
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-dark-textSecondary mt-2">
                {card.hasCustomImage ? 'صورة مخصصة' : 'سيتم استخدام صورة عشوائية من المنتجات'}
              </p>
            </div>

            <div className="mb-4">
              {card.imageUrl ? (
                <img
                  src={getImageUrl(card.imageUrl)}
                  alt={card.title || card.category}
                  className="w-full h-40 object-cover rounded-lg border border-gray-200 dark:border-dark-border"
                />
              ) : (
                <div className="w-full h-40 rounded-lg border border-dashed border-gray-300 dark:border-dark-border bg-gray-50 dark:bg-dark-hover flex items-center justify-center text-gray-500 dark:text-dark-textSecondary">
                  <ImageIcon className="h-5 w-5 ml-2" />
                  بدون صورة
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => saveCard(card)}
              disabled={savingCategory === card.category}
              className="w-full bg-pink-600 hover:bg-pink-700 disabled:opacity-60 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center transition-colors"
            >
              <Save className="h-4 w-4 ml-2" />
              {savingCategory === card.category ? 'جاري الحفظ...' : 'حفظ'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminCategoryCards;
